import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Switch,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  listPeriodicTasks,
  periodicTaskOptions,
  runPeriodicTask,
  updatePeriodicTask,
} from "../../../distflowAPI/periodicTasksApi";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import "./scheduledTasks.css";
import TaskEditor from "./TaskEditor";
import { displayTime, errorMessage } from "./scheduleHelpers";

export default function ScheduledTasks() {
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ tasks: [], timezones: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(undefined);
  const [runTarget, setRunTarget] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [rows, metadata] = await Promise.all([
        listPeriodicTasks(),
        periodicTaskOptions(),
      ]);
      setItems(rows.data);
      setOptions(metadata.data);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "Scheduled Tasks is not available on the connected backend yet. The backend changes and migration must be deployed before tasks can be managed."
          : errorMessage(err)
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function toggle(item) {
    setBusy(item.id);
    setError("");
    setNotice("");
    try {
      const { data } = await updatePeriodicTask(item.id, {
        enabled: !item.enabled,
      });
      setItems((current) =>
        current.map((row) => (row.id === item.id ? data : row))
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }
  async function run() {
    setBusy(runTarget.id);
    setError("");
    setNotice("");
    try {
      const { data } = await runPeriodicTask(runTarget.id);
      setNotice(data.detail);
      setRunTarget(null);
    } catch (err) {
      setError(errorMessage(err));
      setRunTarget(null);
    } finally {
      setBusy(null);
    }
  }
  const visible = items.filter(
    (item) =>
      `${item.name} ${item.task}`
        .toLowerCase()
        .includes(search.toLowerCase()) &&
      (status === "all" || (status === "active" ? item.enabled : !item.enabled))
  );
  return (
    <Stack spacing={2.5} className="scheduled-tasks">
      <Alert
        severity="error"
        variant="outlined"
        sx={{
          bgcolor: "#fff5f5",
          borderColor: "#efb4b4",
          borderLeft: "4px solid #c62828",
          borderRadius: 2,
        }}
      >
        <AlertTitle sx={{ fontWeight: 800, letterSpacing: "0.04em" }}>
          DANGER ZONE
        </AlertTitle>
        These schedules control automated emails, reminders, orders, and
        background processing. Changes take effect on the running scheduler.
      </Alert>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h6">Scheduled Tasks</Typography>
          <Typography variant="body2" color="text.secondary">
            {items.length} schedules ·{" "}
            {items.filter((item) => item.enabled).length} active ·{" "}
            {items.filter((item) => !item.enabled).length} paused
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading || busy !== null}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setEditing(null)}
            disabled={loading || !!error || busy !== null}
          >
            Create schedule
          </Button>
        </Stack>
      </Stack>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" onClose={() => setNotice("")}>
          {notice}
        </Alert>
      ) : null}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="space-between"
      >
        <TextField
          label="Search scheduled tasks"
          placeholder="Search by name or task"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: "100%", sm: 360 } }}
        />
        <Box
          className="schedule-filters"
          role="group"
          aria-label="Filter by status"
        >
          {[
            ["all", "All"],
            ["active", "Active"],
            ["paused", "Paused"],
          ].map(([value, label]) => (
            <Button
              key={value}
              aria-pressed={status === value}
              onClick={() => setStatus(value)}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Stack>
      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={22} />
          <Typography>Loading schedules…</Typography>
        </Stack>
      ) : (
        <TableContainer className="schedule-table-container">
          <Table
            size="small"
            className="schedule-table"
            aria-label="Scheduled tasks"
          >
            <TableHead>
              <TableRow>
                {["Task", "Schedule", "Status", "Run times", "Actions"].map(
                  (label) => (
                    <TableCell key={label}>{label}</TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell data-label="Task">
                    <Typography variant="body2" fontWeight={600}>
                      {item.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      className="schedule-task-path"
                      title={item.task}
                    >
                      {item.task}
                    </Typography>
                  </TableCell>
                  <TableCell data-label="Schedule">
                    <Typography variant="body2">
                      {item.schedule_description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.schedule.type === "cron"
                        ? "Calendar"
                        : item.schedule.type === "interval"
                          ? "Interval"
                          : "Advanced"}
                      {item.schedule.timezone
                        ? ` · ${item.schedule.timezone}`
                        : ""}
                    </Typography>
                  </TableCell>
                  <TableCell data-label="Status">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Switch
                        size="small"
                        checked={item.enabled}
                        disabled={busy !== null}
                        inputProps={{
                          "aria-label": `${item.enabled ? "Disable" : "Enable"} ${item.name}`,
                          role: "switch",
                        }}
                        onChange={() => toggle(item)}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: item.enabled ? "#16704a" : "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        {item.enabled ? "Active" : "Paused"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell data-label="Run times">
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                    >
                      Next scheduled
                    </Typography>
                    <Typography variant="body2">
                      {item.enabled
                        ? displayTime(item.next_run_at, item.schedule.timezone)
                        : "Paused"}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Last:{" "}
                      {displayTime(item.last_run_at, item.schedule.timezone)}
                    </Typography>
                  </TableCell>
                  <TableCell data-label="Actions">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={
                          busy !== null || item.schedule.type === "unsupported"
                        }
                        onClick={() => setEditing(item)}
                      >
                        Edit
                      </Button>
                      <Tooltip title="Run now">
                        <span>
                          <IconButton
                            aria-label="Run now"
                            disabled={busy !== null}
                            onClick={() => setRunTarget(item)}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!visible.length ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 4, textAlign: "center" }}>
                    {search || status !== "all"
                      ? "No schedules match your search."
                      : "No scheduled tasks yet."}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary">
        Scheduled run times refer to queueing, not completion. Interval
        timestamps use your browser timezone; cron timestamps use the schedule
        timezone. Pausing stops future scheduling; it does not cancel work
        already queued.
      </Typography>
      {editing !== undefined ? (
        <TaskEditor
          item={editing}
          tasks={options.tasks}
          timezones={options.timezones}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            setNotice("Schedule saved.");
            load();
          }}
        />
      ) : null}
      <Dialog
        open={!!runTarget}
        onClose={busy !== null ? undefined : () => setRunTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run this task now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Queue “{runTarget?.name}” once with its saved parameters. This can
            send messages or modify business data immediately. Its recurring
            schedule and enabled status will stay unchanged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy !== null} onClick={() => setRunTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy !== null}
            onClick={run}
          >
            {busy !== null ? "Queueing…" : "Run now"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
